/* eslint-disable no-use-before-define */
/**
 * TanStack Query hooks for Products
 *
 * This file provides React Query hooks for fetching and mutating product data.
 * These hooks replace the async methods in the Zustand products store.
 *
 * Cross-store synchronization:
 * - getById populates product-sections cache
 * - list and getByBrandId populate media cache
 * - remove and removeBulk remove related product-sections from cache
 *
 * @module products-queries
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ExportKind } from '../../entity/api/export';
import { type Product } from '../../entity/api/product';
import { type ProductSection } from '../../entity/api/product-section';
import { type CreateProductReq, type ProductSortAndFilter } from '../../entity/api/routes/product';
import {
  createProduct,
  deleteProduct,
  deleteProductBulk,
  exportProductToCsv,
  getProductByid,
  getProductsByBrandId,
  getProductsList,
  retryGeneration,
  updateProduct,
  updateProductBulk,
} from '../../sdk/product';
import {
  productSectionsKeys,
  removeProductSectionsByProductIdFromCache,
  setProductSectionsInCache,
} from './product-sections-queries';
import { prefetchUsersByIds } from './users-queries';

// ============ QUERY KEYS ============

export const productsKeys = {
  all: ['products'] as const,
  list: function () {
    return [...productsKeys.all, 'list'] as const;
  },
  byId: function (id: string) {
    return [...productsKeys.all, id] as const;
  },
  byBrandId: function (brandId: string, queryParams?: ProductSortAndFilter) {
    // Include query params in key for proper cache separation
    return queryParams != null
      ? ([...productsKeys.all, 'brand', brandId, queryParams] as const)
      : ([...productsKeys.all, 'brand', brandId] as const);
  },
  byBulkGroupId: function (bulkGroupId: string, queryParams?: ProductSortAndFilter) {
    // Include query params in key for proper cache separation
    return queryParams != null
      ? ([...productsKeys.all, 'bulkGroup', bulkGroupId, queryParams] as const)
      : ([...productsKeys.all, 'bulkGroup', bulkGroupId] as const);
  },
};

// ============ QUERIES ============

/**
 * Fetch a single product by ID
 * Also populates product-sections cache
 *
 * Replaces: getStores().products.getState().getById(id)
 */
export function useProductById(id: string | undefined | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: productsKeys.byId(id ?? ''),
    queryFn: async function () {
      if (id == null) {
        throw new Error('Product ID is required');
      }
      const response = await getProductByid(id);
      const { product, productSections } = response.data;

      // Cache product sections
      setProductSectionsInCache(queryClient, id, productSections);

      return product;
    },
    enabled: id != null && id !== '',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch all products list
 * Also populates media cache
 *
 * Replaces: getStores().products.getState().list()
 */
export function useProductsList() {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: productsKeys.list(),
    queryFn: async function () {
      const response = await getProductsList();
      const { products, medias } = response.data;

      // Cache each product individually
      for (const product of products) {
        queryClient.setQueryData(productsKeys.byId(product._id), product);
      }

      // Cache media - dynamic import to avoid circular dependencies
      if (medias != null && medias.length > 0) {
        import('./media-queries')
          .then(function ({ setMediasInCache }) {
            setMediasInCache(queryClient, medias);
          })
          .catch(function () {
            // media-queries might not exist yet during migration
          });
      }

      // Prefetch users for "created by" display
      const userIds = products.map((p) => p.createdBy).filter(Boolean);
      await prefetchUsersByIds(queryClient, userIds);

      return products;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch products by brand ID
 * Also populates media cache
 *
 * Replaces: getStores().products.getState().getByBrandId(brandId)
 */
export function useProductsByBrandId(
  brandId: string | undefined | null,
  queryParams?: ProductSortAndFilter
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: productsKeys.byBrandId(brandId ?? '', queryParams),
    queryFn: async function () {
      if (brandId == null) {
        throw new Error('Brand ID is required');
      }
      const response = await getProductsByBrandId(brandId, queryParams);
      const { products, medias } = response.data;
      const { totalCount } = response;

      // Cache each product individually
      for (const product of products) {
        queryClient.setQueryData(productsKeys.byId(product._id), product);
      }

      // Cache media - dynamic import to avoid circular dependencies
      if (medias != null && medias.length > 0) {
        import('./media-queries')
          .then(function ({ setMediasInCache }) {
            setMediasInCache(queryClient, medias);
          })
          .catch(function () {
            // media-queries might not exist yet during migration
          });
      }

      // Prefetch users for "created by" display
      const userIds = products.map((p) => p.createdBy).filter(Boolean);
      await prefetchUsersByIds(queryClient, userIds);

      return { products, totalCount };
    },
    enabled: brandId != null && brandId !== '',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ============ MUTATIONS ============

/**
 * Create a new product
 *
 * Replaces: getStores().products.getState().create(data)
 */
export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (data: CreateProductReq['body']) {
      const response = await createProduct(data);
      return response.data.product;
    },
    onSuccess: function (product) {
      // Cache the new product
      queryClient.setQueryData(productsKeys.byId(product._id), product);

      // Invalidate list queries
      void queryClient.invalidateQueries({ queryKey: productsKeys.list() });
      void queryClient.invalidateQueries({
        queryKey: productsKeys.byBrandId(product.brandId),
      });
      if (product.bulkGroupId != null) {
        void queryClient.invalidateQueries({
          queryKey: productsKeys.byBulkGroupId(product.bulkGroupId),
        });
      }
    },
  });
}

/**
 * Update a product
 *
 * Replaces: getStores().products.getState().update(product, sections)
 */
export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      product,
      sections,
    }: {
      product: Product;
      sections?: ProductSection[];
    }) {
      await updateProduct(product, sections);
      return product;
    },
    onMutate: async function ({ product }) {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: productsKeys.byId(product._id) });
      const previousProduct = queryClient.getQueryData<Product>(productsKeys.byId(product._id));
      queryClient.setQueryData(productsKeys.byId(product._id), product);
      return { previousProduct };
    },
    onError: function (_err, { product }, context) {
      // Rollback on error
      if (context?.previousProduct != null) {
        queryClient.setQueryData(productsKeys.byId(product._id), context.previousProduct);
      }
    },
    onSuccess: function (product) {
      // Update in list caches
      updateProductInListCaches(queryClient, product);
    },
  });
}

/**
 * Update products in bulk
 *
 * Replaces: getStores().products.getState().updateInBulk(products, sections)
 */
export function useUpdateProductBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      products,
      sections,
    }: {
      products: Product[];
      sections?: ProductSection[];
    }) {
      await updateProductBulk(products, sections);
      return products;
    },
    onSuccess: function (products) {
      // Update each product in cache
      for (const product of products) {
        queryClient.setQueryData(productsKeys.byId(product._id), product);
        updateProductInListCaches(queryClient, product);
      }
    },
  });
}

/**
 * Retry product generation
 *
 * Replaces: getStores().products.getState().retryGeneration(product)
 */
export function useRetryProductGeneration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (product: Product) {
      const response = await retryGeneration(product._id);
      return response.data.product;
    },
    onSuccess: function (product) {
      // Update the product in cache
      queryClient.setQueryData(productsKeys.byId(product._id), product);
      updateProductInListCaches(queryClient, product);
    },
  });
}

/**
 * Remove a product
 * Also removes related product-sections from cache
 *
 * Replaces: getStores().products.getState().remove(id)
 */
export function useRemoveProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (id: string) {
      await deleteProduct(id);
      return id;
    },
    onSuccess: function (id) {
      // Get product before removing
      const product = queryClient.getQueryData<Product>(productsKeys.byId(id));

      // Remove from cache
      queryClient.removeQueries({ queryKey: productsKeys.byId(id) });

      // Remove related product sections
      removeProductSectionsByProductIdFromCache(queryClient, id);

      // Update list caches
      if (product != null) {
        removeProductFromListCaches(queryClient, product);
      }
    },
  });
}

/**
 * Remove products in bulk
 * Also removes related product-sections from cache
 *
 * Replaces: getStores().products.getState().removeBulk(ids)
 */
export function useRemoveProductBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (ids: string[]) {
      await deleteProductBulk(ids);
      return ids;
    },
    onSuccess: function (ids) {
      for (const id of ids) {
        // Get product before removing
        const product = queryClient.getQueryData<Product>(productsKeys.byId(id));

        // Remove from cache
        queryClient.removeQueries({ queryKey: productsKeys.byId(id) });

        // Remove related product sections
        removeProductSectionsByProductIdFromCache(queryClient, id);

        // Update list caches
        if (product != null) {
          removeProductFromListCaches(queryClient, product);
        }
      }
    },
  });
}

/**
 * Export products to CSV
 *
 * Replaces: getStores().products.getState().export(ids, kind, productName)
 */
export function useExportProducts() {
  return useMutation({
    mutationFn: async function ({
      ids,
      kind,
      productName,
    }: {
      ids: string[];
      kind: ExportKind;
      productName?: string;
    }) {
      if (kind !== ExportKind.CSV) {
        return '';
      }
      return exportProductToCsv(ids, productName);
    },
  });
}

// ============ CACHE HELPERS ============

/**
 * Get product from cache by ID
 */
export function getProductFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): Product | undefined {
  return queryClient.getQueryData<Product>(productsKeys.byId(id));
}

/**
 * Get products list from cache
 */
export function getProductsListFromCache(
  queryClient: ReturnType<typeof useQueryClient>
): Product[] | undefined {
  return queryClient.getQueryData<Product[]>(productsKeys.list());
}

/**
 * Get products by brand ID from cache
 */
export function getProductsByBrandIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  brandId: string,
  queryParams?: ProductSortAndFilter
): { products: Product[]; totalCount: number } | undefined {
  return queryClient.getQueryData<{ products: Product[]; totalCount: number }>(
    productsKeys.byBrandId(brandId, queryParams)
  );
}

/**
 * Get products by bulk group ID from cache (client-side filter)
 */
export function getProductsByBulkGroupIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  bulkGroupId: string
): Product[] {
  const allProducts = getProductsListFromCache(queryClient) ?? [];
  return allProducts.filter(function (p) {
    return p.bulkGroupId === bulkGroupId;
  });
}

/**
 * Update a product in list caches
 */
function updateProductInListCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  product: Product
): void {
  // Update in list
  queryClient.setQueryData<Product[]>(productsKeys.list(), function (oldList) {
    if (oldList == null) {
      return oldList;
    }
    return oldList.map(function (p) {
      if (p._id === product._id) {
        return product;
      }
      return p;
    });
  });

  // Invalidate all brand queries (pagination makes direct updates complex)
  void queryClient.invalidateQueries({
    queryKey: [...productsKeys.all, 'brand', product.brandId],
  });

  // Invalidate all bulk group queries (pagination makes direct updates complex)
  if (product.bulkGroupId != null) {
    void queryClient.invalidateQueries({
      queryKey: [...productsKeys.all, 'bulkGroup', product.bulkGroupId],
    });
  }
}

/**
 * Remove a product from list caches
 */
function removeProductFromListCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  product: Product
): void {
  // Remove from list
  queryClient.setQueryData<Product[]>(productsKeys.list(), function (oldList) {
    if (oldList == null) {
      return oldList;
    }
    return oldList.filter(function (p) {
      return p._id !== product._id;
    });
  });

  // Invalidate all brand queries (pagination makes direct updates complex)
  void queryClient.invalidateQueries({
    queryKey: [...productsKeys.all, 'brand', product.brandId],
  });

  // Invalidate all bulk group queries (pagination makes direct updates complex)
  if (product.bulkGroupId != null) {
    void queryClient.invalidateQueries({
      queryKey: [...productsKeys.all, 'bulkGroup', product.bulkGroupId],
    });
  }
}

/**
 * Remove products by IDs from cache (for bulk group removal)
 */
export function removeProductsByIdsFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  ids: string[]
): void {
  for (const id of ids) {
    const product = queryClient.getQueryData<Product>(productsKeys.byId(id));
    queryClient.removeQueries({ queryKey: productsKeys.byId(id) });
    if (product != null) {
      removeProductFromListCaches(queryClient, product);
    }
  }
}

/**
 * Invalidate all product queries
 */
export function invalidateProducts(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: productsKeys.all });
}

/**
 * Invalidate a specific product query
 */
export function invalidateProductById(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): void {
  void queryClient.invalidateQueries({ queryKey: productsKeys.byId(id) });
}

// ============ NEW HOOKS ============

/**
 * Fetch products by bulk group ID
 *
 * Replaces: getStores().products.getState().getByBulkGroupIdFromStore(bulkGroupId)
 */
export function useProductsByBulkGroupId(
  bulkGroupId: string | undefined | null,
  queryParams?: ProductSortAndFilter
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: productsKeys.byBulkGroupId(bulkGroupId ?? '', queryParams),
    queryFn: async function () {
      if (bulkGroupId == null) {
        throw new Error('Bulk Group ID is required');
      }
      // Fetch products via the bulk group getById endpoint with query params
      const { getBulkGroupById } = await import('../../sdk/bulk-group');
      const response = await getBulkGroupById(bulkGroupId, queryParams);
      const { products, medias } = response.data;
      const { totalCount } = response;

      // Cache each product individually
      for (const product of products) {
        queryClient.setQueryData(productsKeys.byId(product._id), product);
      }

      // Cache media - dynamic import to avoid circular dependencies
      if (medias != null && medias.length > 0) {
        import('./media-queries')
          .then(function ({ setMediasInCache }) {
            setMediasInCache(queryClient, medias);
          })
          .catch(function () {
            // media-queries might not exist yet during migration
          });
      }

      return { products, totalCount };
    },
    enabled: bulkGroupId != null && bulkGroupId !== '',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Get product sections by product ID using TanStack Query
 *
 * Replaces: getStores().productSections.getState().getByProductIdFromStore(productId)
 */
export function useProductSectionsByProductId(productId: string | undefined | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: productSectionsKeys.byProductId(productId ?? ''),
    queryFn: function () {
      // Product sections are populated when fetching a product by ID
      // So we just return from cache
      const sections = queryClient.getQueryData<ProductSection[]>(
        productSectionsKeys.byProductId(productId ?? '')
      );
      return sections ?? [];
    },
    enabled: productId != null && productId !== '',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ============ IMPERATIVE FUNCTIONS ============

/**
 * Fetch products list and cache it (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic.
 * Also populates Zustand store for backward compatibility.
 */
export async function fetchProductsListImperative(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<Product[]> {
  const response = await getProductsList();
  const { products, medias } = response.data;

  // Cache in TanStack Query
  queryClient.setQueryData(productsKeys.list(), products);

  // Also cache individual products by ID
  for (const product of products) {
    queryClient.setQueryData(productsKeys.byId(product._id), product);
  }

  // Cache media - must be awaited so cache is populated before function returns
  if (medias != null && medias.length > 0) {
    const { setMediasInCache } = await import('./media-queries');
    setMediasInCache(queryClient, medias);
  }

  // Prefetch users for "created by" display
  const userIds = products.map((p) => p.createdBy).filter(Boolean);
  await prefetchUsersByIds(queryClient, userIds);

  return products;
}

/**
 * Fetch a product by ID and cache it (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic.
 * Also populates product-sections cache.
 */
export async function fetchProductByIdImperative(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): Promise<Product> {
  const response = await getProductByid(id);
  const { product, productSections } = response.data;

  // Cache product in TanStack Query
  queryClient.setQueryData(productsKeys.byId(id), product);

  // Cache product sections
  setProductSectionsInCache(queryClient, id, productSections);

  return product;
}

/**
 * Fetch products by brand ID and cache them (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic.
 * Also populates Zustand store for backward compatibility.
 */
export async function fetchProductsByBrandIdImperative(
  queryClient: ReturnType<typeof useQueryClient>,
  brandId: string,
  queryParams?: ProductSortAndFilter
): Promise<{ products: Product[]; totalCount: number }> {
  const response = await getProductsByBrandId(brandId, queryParams);
  const { products, medias } = response.data;
  const { totalCount } = response;

  // Cache in TanStack Query
  queryClient.setQueryData(productsKeys.byBrandId(brandId, queryParams), {
    products,
    totalCount,
  });

  // Also cache individual products by ID
  for (const product of products) {
    queryClient.setQueryData(productsKeys.byId(product._id), product);
  }

  // Cache media - must be awaited so cache is populated before function returns
  if (medias != null && medias.length > 0) {
    const { setMediasInCache } = await import('./media-queries');
    setMediasInCache(queryClient, medias);
  }

  // Prefetch users for "created by" display
  const userIds = products.map((p) => p.createdBy).filter(Boolean);
  await prefetchUsersByIds(queryClient, userIds);

  return { products, totalCount };
}

/**
 * Get adjacent product ID from cache (for next/prev navigation)
 *
 * Replaces: getStores().products.getState().getAdjacentIdFromStore(direction, origin)
 */
export function getAdjacentProductIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  direction: 'next' | 'prev',
  currentId: string,
  brandId: string
): string | null {
  // Use partial key matching to find any cached brand query, regardless of queryParams.
  // getProductsByBrandIdFromCache(queryClient, brandId) would only match the key without
  // queryParams, which is never populated — the listing always stores with queryParams.
  const brandQueries = queryClient.getQueriesData<{ products: Product[]; totalCount: number }>({
    queryKey: [...productsKeys.all, 'brand', brandId],
  });

  const productsList =
    brandQueries
      .map(function ([, data]) {
        return data?.products ?? [];
      })
      .find(function (products) {
        return products.length > 0;
      }) ?? [];

  let result: string | null = null;

  for (let i = 0; i < productsList.length; i++) {
    if (productsList[i]?._id === currentId) {
      if (direction === 'next' && i !== productsList.length - 1) {
        result = productsList[i + 1]?._id ?? null;
        break;
      }
      if (direction === 'prev' && i !== 0) {
        result = productsList[i - 1]?._id ?? null;
        break;
      }
    }
  }

  return result;
}

/**
 * Set a product in cache (for socket updates)
 */
export function setProductInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  product: Product
): void {
  queryClient.setQueryData(productsKeys.byId(product._id), product);
  updateProductInListCaches(queryClient, product);
}

/**
 * Set product sections in cache (for socket updates)
 */
export function setProductSectionsInCacheForSocket(
  queryClient: ReturnType<typeof useQueryClient>,
  productId: string,
  sections: ProductSection[]
): void {
  setProductSectionsInCache(queryClient, productId, sections);
}
